import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import SeoHead from "@/components/SeoHead";
import FinalCta from "@/components/sections/FinalCta";

const BG =
  "https://images.unsplash.com/photo-1517502884422-41eaead166d4?auto=format&fit=crop&w=2200&q=80";

export default function Expertise() {
  const { t } = useI18n();
  const topics = t("expertise_page.topics");
  const questions = t("expertise_page.questions");

  return (
    <div data-testid={TID.pageExpertise}>
      <SeoHead titleKey="seo.expertise_title" descKey="seo.expertise_desc" />

      <section className="relative bg-[color:var(--gc-navy)] text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={BG} alt="" className="w-full h-full object-cover gc-kenburns" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--gc-navy)] via-[color:var(--gc-navy)]/85 to-transparent" />
        <div className="absolute inset-0 gc-grid-bg-dark opacity-60" />

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <p className="gc-eyebrow text-white/60">{t("expertise_page.eyebrow")}</p>
            <h1 className="mt-6 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.96] tracking-tight font-[700]">
              {t("expertise_page.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
              {t("expertise_page.lead")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="border-t border-[color:var(--gc-line)]">
                {Array.isArray(topics) &&
                  topics.map((tp, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.5, delay: 0.04 * (i % 4) }}
                      className="border-b border-[color:var(--gc-line)] py-9 md:py-10 group grid grid-cols-12 gap-6"
                    >
                      <div className="col-span-12 md:col-span-2 flex items-start">
                        <span className="font-mono text-sm tracking-[0.16em] text-[color:var(--gc-mist)]">
                          / {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="col-span-12 md:col-span-10">
                        <h2 className="font-display text-2xl md:text-3xl font-[700] tracking-tight text-[color:var(--gc-ink)] leading-tight">
                          {tp.t}
                        </h2>
                        <p className="mt-3 text-[15px] md:text-base leading-relaxed text-[color:var(--gc-graphite)] max-w-2xl">
                          {tp.d}
                        </p>
                        <span className="block mt-5 w-0 h-px bg-[color:var(--gc-red)] group-hover:w-16 transition-all duration-500" />
                      </div>
                    </motion.div>
                  ))}
              </div>
            </div>

            <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
              <div className="bg-[color:var(--gc-ink)] text-white p-8 md:p-10">
                <p className="gc-eyebrow text-white/50">
                  {t("expertise_page.questions_title")}
                </p>
                <ul className="mt-8 space-y-4">
                  {Array.isArray(questions) &&
                    questions.map((q, i) => (
                      <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 bg-[color:var(--gc-red)] shrink-0" />
                        <span className="text-white/85">{q}</span>
                      </li>
                    ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <FinalCta />
    </div>
  );
}
